import {Component, ViewChild} from '@angular/core';
import {SelectDropdownComponent} from '../../elements/select-dropdown/select-dropdown.component';
import {FormsModule, NgForm} from '@angular/forms';
import {NgIf} from '@angular/common';
import { LanguageService } from '../../../services/language.service';

@Component({
  selector: 'app-contact-us',
  imports: [
    SelectDropdownComponent,
    FormsModule,
    NgIf
  ],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss'
})
export class ContactUsComponent {
  @ViewChild('contactForm') contactForm!: NgForm;

  constructor(public languageService: LanguageService) {}

  fullName: string = '';
  company: string = '';
  phoneNumber: string = '';
  email: string = '';
  selectedHearAbout: string = '';
  message: string = '';
  isConsentGiven: boolean = false;


  phoneError: string = '';
  emailError: string = '';

  hearAboutOptions = [
    { value: 'sub1', label: 'Var1' },
    { value: 'sub2', label: 'Var2' },
    { value: 'sub3', label: 'Var3' },
    { value: 'sub4', label: 'Var4' },
    { value: 'sub5', label: 'Var5' }
  ];

  onHearAboutChange(value: string) {
    this.selectedHearAbout = value;
    console.log('Selected option:', value);
  }

  autoResize(event: any) {
    const textarea = event.target;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }


  isPhoneValid(): boolean {
    if (!this.phoneNumber) return false;

    const digitsOnly = this.phoneNumber.replace(/\D/g, '');

    const hasCountryCode = this.phoneNumber.startsWith('+');

    return hasCountryCode && digitsOnly.length >= 8 && digitsOnly.length <= 15;
  }


  isEmailValid(): boolean {
    if (!this.email) return false;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(this.email);
  }


  isFormValid(): boolean {
    return !!(
      this.fullName?.trim() &&
      this.company?.trim() &&
      this.isPhoneValid() &&
      this.isEmailValid() &&
      this.selectedHearAbout &&
      this.message?.trim() &&
      this.isConsentGiven
    );
  }


  formatPhoneNumber(event: any) {
    let input = event.target;
    let value = input.value;
    let digits = value.replace(/\D/g, '');

    if (digits.length > 15) {
      digits = digits.substring(0, 15);
    }

    let cursorPosition = input.selectionStart;
    let oldLength = value.length;
    let formattedNumber = this.applyPhoneMask(digits);

    this.phoneNumber = formattedNumber;
    input.value = formattedNumber;

    let newLength = formattedNumber.length;
    let newCursorPosition = cursorPosition + (newLength - oldLength);

    newCursorPosition = Math.min(newCursorPosition, newLength);
    newCursorPosition = Math.max(0, newCursorPosition);

    input.setSelectionRange(newCursorPosition, newCursorPosition);

    this.validatePhoneNumber();
  }

  private applyPhoneMask(digits: string): string {
    if (!digits) return '';

    if (digits.startsWith('8') || digits.startsWith('7')) {
      return this.formatRussianNumber(digits);
    }
    return this.formatInternationalNumber(digits);
  }

  private formatRussianNumber(digits: string): string {
    let numberWithoutFirst = digits.substring(1);

    if (numberWithoutFirst.length > 10) {
      numberWithoutFirst = numberWithoutFirst.substring(0, 10);
    }
    let result = '+7';

    if (numberWithoutFirst.length > 0) {
      result += ' (';
      result += numberWithoutFirst.substring(0, Math.min(3, numberWithoutFirst.length));

      if (numberWithoutFirst.length > 3) {
        result += ') ';
        result += numberWithoutFirst.substring(3, Math.min(6, numberWithoutFirst.length));

        if (numberWithoutFirst.length > 6) {
          result += '-';
          result += numberWithoutFirst.substring(6, Math.min(10, numberWithoutFirst.length));
        }
      }
    }

    return result;
  }

  private formatInternationalNumber(digits: string): string {
    if (digits.length <= 3) {
      return '+' + digits;
    }

    let countryCode = '';
    let restNumber = '';

    if (digits.length >= 1) {

      if (digits.startsWith('1')) { // USA/Canada
        countryCode = '1';
        restNumber = digits.substring(1);
        return this.formatUSNumber(countryCode, restNumber);
      } else if (digits.startsWith('44')) { // UK
        countryCode = '44';
        restNumber = digits.substring(2);
        return this.formatUKNumber(countryCode, restNumber);
      } else if (digits.startsWith('49')) { // Germany
        countryCode = '49';
        restNumber = digits.substring(2);
        return this.formatGenericNumber(countryCode, restNumber);
      } else {
        countryCode = digits.substring(0, Math.min(3, digits.length));
        restNumber = digits.substring(countryCode.length);
        return this.formatGenericNumber(countryCode, restNumber);
      }
    }
    return '+' + digits;
  }

  private formatUSNumber(countryCode: string, digits: string): string {
    let result = '+' + countryCode;

    if (digits.length > 0) {
      result += ' (';
      result += digits.substring(0, Math.min(3, digits.length));

      if (digits.length > 3) {
        result += ') ';
        result += digits.substring(3, Math.min(6, digits.length));

        if (digits.length > 6) {
          result += '-';
          result += digits.substring(6, Math.min(10, digits.length));
        }
      }
    }

    return result;
  }

  private formatUKNumber(countryCode: string, digits: string): string {
    let result = '+' + countryCode;

    if (digits.length > 0) {
      result += ' ';
      result += digits.substring(0, Math.min(4, digits.length));

      if (digits.length > 4) {
        result += ' ';
        result += digits.substring(4, Math.min(7, digits.length));

        if (digits.length > 7) {
          result += ' ';
          result += digits.substring(7, Math.min(10, digits.length));
        }
      }
    }

    return result;
  }

  private formatGenericNumber(countryCode: string, digits: string): string {
    let result = '+' + countryCode;

    if (digits.length > 0) {
      result += ' ';

      for (let i = 0; i < digits.length; i += 3) {
        if (i > 0) result += ' ';
        result += digits.substring(i, Math.min(i + 3, digits.length));
      }
    }

    return result;
  }

  validatePhoneNumber() {
    if (this.phoneNumber) {
      const digitsOnly = this.phoneNumber.replace(/\D/g, '');

      if (!this.phoneNumber.startsWith('+')) {
        this.phoneError = 'countryCode';
      }
      else if (digitsOnly.length < 8) {
        this.phoneError = 'tooShort';
      }
      else if (digitsOnly.length > 15) {
        this.phoneError = 'tooLong';
      }
      else {
        this.phoneError = '';
      }
    } else {
      this.phoneError = '';
    }
  }

  validateEmail(event: any) {
    const email = event.target.value;
    this.email = email;

    if (email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      if (!emailRegex.test(email)) {
        this.emailError = 'invalidEmail';
      } else {
        this.emailError = '';
      }
    } else {
      this.emailError = '';
    }
  }


  onSubmit() {
    if (this.isFormValid()) {

      const formData = {
        personalInfo: {
          fullName: this.fullName,
          company: this.company,
          phoneNumber: this.phoneNumber,
          email: this.email
        },
        requestDetails: {
          subject: this.selectedHearAbout,
          message: this.message
        },
        consent: this.isConsentGiven,
        timestamp: new Date().toISOString(),
        formId: this.generateFormId()
      };

      console.log('========== FORM SUBMISSION ==========');
      console.log('Form Data:', formData);
      console.log('Raw Form Values:', {
        fullName: this.fullName,
        company: this.company,
        phoneNumber: this.phoneNumber,
        email: this.email,
        selectedHearAbout: this.selectedHearAbout,
        message: this.message,
        isConsentGiven: this.isConsentGiven
      });
      console.log('Form Valid Status:', {
        isFormValid: this.isFormValid(),
        phoneValid: this.isPhoneValid(),
        emailValid: this.isEmailValid()
      });
      console.log('=====================================');

      // this.sendToServer(formData);

      alert(this.languageService.translate([
        'Form submitted successfully! Check console for details.',
        'Форма успешно отправлена! Подробности в консоли.'
      ]));

      // this.resetForm();
    }
  }


  private generateFormId(): string {
    return 'FORM_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private sendToServer(formData: any) {

  }

  // Опционально: сброс формы
  resetForm() {
    this.fullName = '';
    this.company = '';
    this.phoneNumber = '';
    this.email = '';
    this.selectedHearAbout = '';
    this.message = '';
    this.isConsentGiven = false;
    this.phoneError = '';
    this.emailError = '';

    // Сброс Angular формы
    this.contactForm?.resetForm();
  }

  getPhoneErrorText(): string {
    if (!this.phoneError) return '';

    if (this.phoneError === 'countryCode') {
      return this.languageService.translate([
        'Phone number must start with country code (+)',
        'Номер телефона должен начинаться с кода страны (+)'
      ]);
    }

    if (this.phoneError === 'tooShort') {
      return this.languageService.translate([
        'Phone number is too short (minimum 8 digits)',
        'Номер телефона слишком короткий, минимум 8 цифр'
      ]);
    }

    if (this.phoneError === 'tooLong') {
      return this.languageService.translate([
        'Phone number is too long (maximum 15 digits)',
        'Номер телефона слишком длинный, максимум 15 цифр'
      ]);
    }

    return this.phoneError;
  }

  getEmailErrorText(): string {
    if (!this.emailError) return '';

    return this.languageService.translate([
      'Please enter a valid email address (e.g., name@domain.com)',
      'Введите корректный email, например name@domain.com'
    ]);
  }
}
